import { useHandler } from "@redotech/react-util/hook";
import {
  rcoProductVendorSegmentSchema,
  RcoProductVendorSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import {
  SegmentListMultipleItemMembershipOperator,
  SegmentListSingleItemMembershipOperator,
} from "@redotech/redo-model/checkouts/checkout-tree";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { OperatorSelector } from "@redotech/redo-web/conversion/operator-selector";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";
import { ProductVendorSelector } from "./product-vendor-selector";

export const ProductVendorInspectorContent = memo(
  function ProductVendorInspectorContent({ step }: { step: RcoSegmentStep }) {
    const segment = useMemo(
      () => rcoProductVendorSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {operatorsToText(segment.cartOperator, segment.operator)}
          </Text>
        </Flex>
        <Flex dir="row" gap="md" wrap="wrap">
          {segment.vendors.map((vendor, index) => (
            <RedoBadge color="gray" key={index} size="sm" text={vendor} />
          ))}
        </Flex>
      </>
    );
  },
);

const operatorsToText = (
  cartOperator: RcoProductVendorSegmentSchema["cartOperator"],
  operator: RcoProductVendorSegmentSchema["operator"],
) => {
  switch (cartOperator) {
    case SegmentListMultipleItemMembershipOperator.ANY_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return "The vendor for at least one product in the cart is in the list:";
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return "The vendor for at least one product in the cart is not in the list:";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.ALL_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return "The vendor for every product in the cart is in the list:";
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return "The vendor for every product in the cart is not in the list:";
        default:
          assertNever(operator);
      }
      break;
    case SegmentListMultipleItemMembershipOperator.NONE_IN:
      switch (operator) {
        case SegmentListSingleItemMembershipOperator.IN:
          return "The vendor for every product in the cart is not in the list:";
        case SegmentListSingleItemMembershipOperator.NOT_IN:
          return "The vendor for every product in the cart is in the list:";
        default:
          assertNever(operator);
      }
      break;
    default:
      assertNever(cartOperator);
  }
};

export const segmentListMultipleItemMembershipOperatorToText: Record<
  SegmentListMultipleItemMembershipOperator,
  string
> = {
  [SegmentListMultipleItemMembershipOperator.ANY_IN]: "at least one",
  [SegmentListMultipleItemMembershipOperator.NONE_IN]: "no",
  [SegmentListMultipleItemMembershipOperator.ALL_IN]: "every",
};

export const segmentListSingleItemMembershipOperatorToText: Record<
  SegmentListSingleItemMembershipOperator,
  string
> = {
  [SegmentListSingleItemMembershipOperator.IN]: "is",
  [SegmentListSingleItemMembershipOperator.NOT_IN]: "is not",
};

export const EditableProductVendorInspectorContent = memo(
  function EditableProductVendorInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const segment = useMemo(
      () => rcoProductVendorSegmentSchema.parse(step.segment),
      [step],
    );

    const handleCartOperatorSelected = useHandler(
      (cartOperator: SegmentListMultipleItemMembershipOperator) => {
        const updatedSegment = { ...segment, cartOperator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleOperatorSelected = useHandler(
      (operator: SegmentListSingleItemMembershipOperator) => {
        const updatedSegment = { ...segment, operator };
        setStep({ ...step, segment: updatedSegment });
      },
    );

    const handleSetVendors = useHandler((vendors: string[]) => {
      setStep({ ...step, segment: { ...segment, vendors } });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            Condition
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            The vendor for
          </Text>
        </Flex>
        <Flex>
          <OperatorSelector<SegmentListMultipleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListMultipleItemMembershipOperator,
            )}
            onOperatorSelected={handleCartOperatorSelected}
            operatorToText={segmentListMultipleItemMembershipOperatorToText}
            selectedOperator={segment.cartOperator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          product in the cart
        </Text>
        <Flex>
          <OperatorSelector<SegmentListSingleItemMembershipOperator>
            availableOptions={Object.values(
              SegmentListSingleItemMembershipOperator,
            )}
            onOperatorSelected={handleOperatorSelected}
            operatorToText={segmentListSingleItemMembershipOperatorToText}
            selectedOperator={segment.operator}
          />
        </Flex>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          in the list:
        </Text>
        <ProductVendorSelector
          onVendorsSelected={handleSetVendors}
          selectedVendors={segment.vendors}
        />
      </>
    );
  },
);
